import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, ClockIcon, EyeIcon } from '@heroicons/react/24/solid';
import { UserContext } from '../context/UserContext';
import FiscalYearDialog from '../components/FiscalYearDialog';
import PeriodClosingDialog from '../components/PeriodClosingDialog';
import PeriodClosingDetailDialog from '../components/PeriodClosingDetailDialog';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { usePeriodClosingStore } from '../stores/periodClosingStore';
import { reopenPeriod, deleteAccountingPeriod, deleteFiscalYear, clearPeriodClosings, getPeriodClosingById } from '../services/api';
import { toast } from 'react-hot-toast';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell, ConfirmModal, Button } from '@redpanda/ui';
import { TableSkeleton } from '@redpanda/ui';
import { Role } from '@redpanda/types';

const PeriodManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const { user } = userContext!;
  const userRole = user?.role;

  const [isFiscalYearDialogOpen, setIsFiscalYearDialogOpen] = useState(false);
  const [isPeriodClosingDialogOpen, setIsPeriodClosingDialogOpen] = useState(false);
  const [isPeriodClosingDetailDialogOpen, setIsPeriodClosingDetailDialogOpen] = useState(false);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<any>(null);
  const [selectedPeriodClosing, setSelectedPeriodClosing] = useState<any>(null);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [periodToReopen, setPeriodToReopen] = useState<any>(null);
  const [reopening, setReopening] = useState(false);
  // const [generatingPeriods, setGeneratingPeriods] = useState<string | null>(null); // 移除：期間自動生成
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteFiscalYearModal, setShowDeleteFiscalYearModal] = useState(false);
  const [fiscalYearToDelete, setFiscalYearToDelete] = useState<any>(null);
  const [deletingFiscalYear, setDeletingFiscalYear] = useState(false);
  
  // 開發工具狀態
  const [showDevClearModal, setShowDevClearModal] = useState(false);
  const [clearingData, setClearingData] = useState(false);
  const isDevEnv = process.env.NODE_ENV === 'development';

  const {
    fiscalYears,
    pagination: fiscalYearPagination,
    isLoading: fiscalYearLoading,
    fetchFiscalYears,
    refreshData: refreshFiscalYears,
  } = useFiscalYearStore();

  const {
    periodClosings,
    pagination: closingPagination,
    isLoading: closingLoading,
    fetchPeriodClosings,
    addPeriodClosing,
    refreshData: refreshPeriodClosings,
    updatePeriodClosing,
  } = usePeriodClosingStore();

  const canEdit = userRole && [Role.SUPER_ADMIN, Role.ADMIN].includes(userRole);

  useEffect(() => {
    fetchFiscalYears(1);
    fetchPeriodClosings(1);
  }, [fetchFiscalYears, fetchPeriodClosings]);

  const handleEditFiscalYear = (fiscalYear: any) => {
    setSelectedFiscalYear(fiscalYear);
    setIsFiscalYearDialogOpen(true);
  };

  const handleCloseFiscalYearDialog = () => {
    setIsFiscalYearDialogOpen(false);
    setSelectedFiscalYear(null);
  };

  const handleFiscalYearCreated = (_savedFiscalYear: any, _isEdit: boolean) => {
    // 樂觀更新已在 FiscalYearDialog 中完成，這裡只關閉對話框
    handleCloseFiscalYearDialog();
  };

  const handleClosingStarted = (closing: any) => {
    // 如果 closing 是 undefined 或 null，說明是錯誤情況，不要關閉對話框
    if (!closing) {
      console.log('handleClosingStarted called with invalid closing data, ignoring');
      return;
    }

    // 樂觀更新：立即添加新的期間結算記錄
    addPeriodClosing(closing);
    setIsPeriodClosingDialogOpen(false);

    // 重新載入所有相關資料以確保狀態同步（使用 refreshData 強制重新載入，避免快取問題）
    refreshFiscalYears();  // 強制重新載入會計年度和期間狀態
    fetchPeriodClosings(closingPagination.currentPage);  // 確保期間結算列表完整更新
  };

  // 處理期間結算詳細檢視
  const handleViewPeriodClosingDetail = async (closing: any) => {
    try {
      // 調用 API 獲取包含 closingEntries 的完整資料
      const response = await getPeriodClosingById(closing.id);
      setSelectedPeriodClosing(response.data);
      setIsPeriodClosingDetailDialogOpen(true);
    } catch (error) {
      console.error('獲取期間結算詳情失敗:', error);
      toast.error(t('periods.toast.getDetailFailed'));
    }
  };

  const handleClosePeriodClosingDetailDialog = () => {
    setSelectedPeriodClosing(null);
    setIsPeriodClosingDetailDialogOpen(false);
  };

  // 處理備註更新
  const handleNotesUpdated = (updatedClosing: any) => {
    // 樂觀更新 store 中的期間結算記錄
    updatePeriodClosing(updatedClosing.id, updatedClosing);
    // 更新目前選中的記錄
    setSelectedPeriodClosing(updatedClosing);
  };

  // handleGeneratePeriods 已移除：期間會在創建會計年度時自動生成

  // 開發工具：清理期間結算記錄
  const handleClearPeriodClosings = () => {
    setShowDevClearModal(true);
  };

  const confirmClearPeriodClosings = async () => {
    setClearingData(true);
    try {
      await clearPeriodClosings();
      toast.success(t('periods.toast.clearSuccess'));
      setShowDevClearModal(false);
      // 重新載入資料
      fetchFiscalYears(fiscalYearPagination.currentPage);
      fetchPeriodClosings(closingPagination.currentPage);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          '清理期間結算記錄失敗';
      toast.error(t('periods.toast.clearFailed', { error: errorMessage }));
    } finally {
      setClearingData(false);
    }
  };

  const handleReopenPeriod = (period: any) => {
    setPeriodToReopen(period);
    setShowReopenModal(true);
  };

  const confirmReopenPeriod = async () => {
    if (!periodToReopen) return;

    setReopening(true);
    try {
      await reopenPeriod(periodToReopen.id);
      
      // 檢查是否重新開啟了整年度
      const isYearEndPeriod = periodClosings?.some(closing => 
        closing.periodId === periodToReopen.id && 
        closing.type === 'YEARLY' && 
        closing.status === 'COMPLETED'
      );
      
      const hasYearEndInFiscalYear = periodClosings?.some(closing =>
        closing.period?.fiscalYear?.id === periodToReopen?.fiscalYear?.id &&
        closing.type === 'YEARLY' &&
        closing.status === 'COMPLETED'
      );
      
      const hasPeriodClosing = periodClosings?.some(closing =>
        closing.periodId === periodToReopen?.id &&
        closing.status === 'COMPLETED'
      );
      
      const isClosedByYearEnd = hasYearEndInFiscalYear && !hasPeriodClosing;
      
      if (isYearEndPeriod || isClosedByYearEnd) {
        toast.success(t('periods.toast.reopenYearSuccess', {
          year: periodToReopen.fiscalYear?.name || '-'
        }));
      } else {
        toast.success(t('periods.toast.reopenPeriodSuccess', { name: periodToReopen.name }));
      }
      
      setShowReopenModal(false);
      setPeriodToReopen(null);
      // 使用 refresh 函數強制重新載入數據，確保狀態更新
      refreshFiscalYears();
      refreshPeriodClosings(); // 強制刷新期間結算記錄快取
    } catch (error: any) {
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          '重新開啟期間失敗';
      toast.error(t('periods.toast.reopenFailed', { error: errorMessage }));
    } finally {
      setReopening(false);
    }
  };

  const handleDeletePeriod = (period: any) => {
    setPeriodToDelete(period);
    setShowDeleteModal(true);
  };

  const confirmDeletePeriod = async () => {
    if (!periodToDelete) return;

    setDeleting(true);
    try {
      await deleteAccountingPeriod(periodToDelete.id);
      toast.success(t('periods.toast.deleteSuccess', { name: periodToDelete.name }));
      setShowDeleteModal(false);
      setPeriodToDelete(null);
      fetchFiscalYears(fiscalYearPagination.currentPage);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          '刪除期間失敗';
      toast.error(t('periods.toast.deleteFailed', { error: errorMessage }));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteFiscalYear = (fiscalYear: any) => {
    setFiscalYearToDelete(fiscalYear);
    setShowDeleteFiscalYearModal(true);
  };

  const confirmDeleteFiscalYear = async () => {
    if (!fiscalYearToDelete) return;

    setDeletingFiscalYear(true);
    try {
      await deleteFiscalYear(fiscalYearToDelete.id);
      toast.success(t('periods.toast.deleteFiscalYearSuccess', { name: fiscalYearToDelete.name }));
      setShowDeleteFiscalYearModal(false);
      setFiscalYearToDelete(null);
      fetchFiscalYears(fiscalYearPagination.currentPage);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          '刪除會計年度失敗';
      toast.error(t('periods.toast.deleteFiscalYearFailed', { error: errorMessage }));
    } finally {
      setDeletingFiscalYear(false);
    }
  };

  // 獲取所有可用的期間
  const getAllAvailablePeriods = () => {
    const periods: any[] = [];
    fiscalYears?.forEach(fy => {
      if (fy.periods && fy.periods.length > 0) {
        fy.periods.forEach(period => {
          periods.push({
            ...period,
            fiscalYear: { name: fy.name }
          });
        });
      }
    });
    return periods;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('zh-TW');
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      OPEN: { label: t('periods.status.OPEN'), className: 'bg-green-100 text-green-800' },
      CLOSED: { label: t('periods.status.CLOSED'), className: 'bg-gray-100 text-gray-800' },
      PENDING: { label: t('periods.status.PENDING'), className: 'bg-gray-100 text-gray-800' },
      PROCESSING: { label: t('periods.status.PROCESSING'), className: 'bg-yellow-100 text-yellow-800' },
      COMPLETED: { label: t('periods.status.COMPLETED'), className: 'bg-blue-100 text-blue-800' },
      FAILED: { label: t('periods.status.FAILED'), className: 'bg-red-100 text-red-800' },
      REOPENED: { label: t('periods.status.REOPENED'), className: 'bg-purple-100 text-purple-800' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] ||
                   { label: status, className: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const isInitialLoading = (fiscalYearLoading && closingLoading);

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-4">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t('periods.pageTitle')}
          </h1>
          <div className="flex space-x-3">
            {canEdit && (
              <>
                <Button
                  onClick={() => setIsFiscalYearDialogOpen(true)}
                  className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('periods.addFiscalYear')}
                </Button>
                <Button
                  onClick={() => setIsPeriodClosingDialogOpen(true)}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
                >
                  <ClockIcon className="h-4 w-4 mr-2" />
                  {t('periods.startClosing')}
                </Button>
                {/* 開發工具按鈕 */}
                {isDevEnv && (
                  <Button
                    onClick={handleClearPeriodClosings}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
                  >
                    {t('periods.devClearClosings')}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* 會計年度列表 */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">{t('periods.fiscalYears')}</h2>
        <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
          <div className="overflow-x-auto">
            {isInitialLoading ? (
              <TableSkeleton rows={5} columns={7} showHeader={true} />
            ) : (
              <Table style={{ tableLayout: 'fixed', minWidth: '800px' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/6">{t('periods.table.yearName')}</TableHead>
                    <TableHead className="w-1/8">{t('periods.table.startDate')}</TableHead>
                    <TableHead className="w-1/8">{t('periods.table.endDate')}</TableHead>
                    <TableHead className="w-1/12">{t('common.table.columns.status')}</TableHead>
                    <TableHead className="w-1/8">{t('periods.table.periodCount')}</TableHead>
                    <TableHead className="w-1/4">{t('periods.table.description')}</TableHead>
                    <TableHead className="w-1/6">{t('common.table.columns.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {fiscalYears?.map((fy) => (
                  <TableRow key={fy.id}>
                    <TableCell className="font-medium">
                      {fy.name}
                      {fy.isActive && (
                        <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-brand-100 text-brand-800">
                          {t('periods.currentYear')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(fy.startDate)}</TableCell>
                    <TableCell>{formatDate(fy.endDate)}</TableCell>
                    <TableCell>
                      {getStatusBadge(fy.isClosed ? 'CLOSED' : 'OPEN')}
                    </TableCell>
                    <TableCell>
                      {t('periods.periodCountValue', { count: fy._count?.periods || 0 })}
                    </TableCell>
                    <TableCell className="truncate" title={fy.description}>
                      {fy.description || t('periods.noDescription')}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-4">
                        {canEdit && (
                          <>
                            <button
                              onClick={() => handleEditFiscalYear(fy)}
                              className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
                            >
                              {t('common.actions.edit')}
                            </button>
                            {/* 期間已自動生成，無需手動按鈕 */}
                            <button
                              onClick={() => handleDeleteFiscalYear(fy)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              {t('common.actions.delete')}
                            </button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>

      {/* 會計期間詳細列表 */}
      {fiscalYears?.some(fy => fy.periods && fy.periods.length > 0) && (
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">{t('periods.accountingPeriods')}</h2>
          <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
            <div className="overflow-x-auto">
              {isInitialLoading ? (
                <TableSkeleton rows={8} columns={6} showHeader={true} />
              ) : (
                <Table style={{ tableLayout: 'fixed', minWidth: '900px' }}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/6">{t('periods.table.fiscalYear')}</TableHead>
                      <TableHead className="w-1/6">{t('periods.table.periodName')}</TableHead>
                      <TableHead className="w-1/8">{t('periods.table.startDate')}</TableHead>
                      <TableHead className="w-1/8">{t('periods.table.endDate')}</TableHead>
                      <TableHead className="w-1/12">{t('common.table.columns.status')}</TableHead>
                      <TableHead className="w-1/6">{t('common.table.columns.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                  {fiscalYears?.map(fy =>
                    fy.periods?.map(period => (
                      <TableRow key={period.id}>
                        <TableCell>{fy.name}</TableCell>
                        <TableCell>{period.name}</TableCell>
                        <TableCell>{formatDate(period.startDate)}</TableCell>
                        <TableCell>{formatDate(period.endDate)}</TableCell>
                        <TableCell>
                          {getStatusBadge(period.status)}
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            {canEdit && period.status === 'CLOSED' && (
                              <button
                                onClick={() => handleReopenPeriod(period)}
                                className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300"
                              >
                                {t('periods.reopenPeriod')}
                              </button>
                            )}
                            {canEdit && period.status === 'OPEN' && (
                              <button
                                onClick={() => handleDeletePeriod(period)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                {t('common.actions.delete')}
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 期間結算記錄 */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">{t('periods.closingRecords')}</h2>
        <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
          <div className="overflow-x-auto">
            {isInitialLoading ? (
              <TableSkeleton rows={6} columns={8} showHeader={true} />
            ) : (
              <Table style={{ tableLayout: 'fixed', minWidth: '900px' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/8">{t('periods.table.fiscalYear')}</TableHead>
                    <TableHead className="w-1/8">{t('periods.table.periodName')}</TableHead>
                    <TableHead className="w-1/12">{t('periods.table.closingType')}</TableHead>
                    <TableHead className="w-1/12">{t('common.table.columns.status')}</TableHead>
                    <TableHead className="w-1/8">{t('periods.table.startTime')}</TableHead>
                    <TableHead className="w-1/8">{t('periods.table.completeTime')}</TableHead>
                    <TableHead className="w-1/6">{t('periods.table.notes')}</TableHead>
                    <TableHead className="w-1/8">{t('common.table.columns.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {periodClosings?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                      {t('periods.noClosingRecords')}
                    </TableCell>
                  </TableRow>
                ) : (
                  periodClosings?.map((closing) => (
                    <TableRow key={closing.id}>
                      <TableCell>{closing.period?.fiscalYear?.name || '-'}</TableCell>
                      <TableCell>{closing.period?.name}</TableCell>
                      <TableCell>
                        {t(`periods.closingType.${closing.type}`)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(closing.status)}
                      </TableCell>
                      <TableCell>{formatDate(closing.startedAt)}</TableCell>
                      <TableCell>
                        {closing.completedAt ? formatDate(closing.completedAt) : '-'}
                      </TableCell>
                      <TableCell className="truncate" title={closing.notes}>
                        {closing.notes || '-'}
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleViewPeriodClosingDetail(closing)}
                          className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300 inline-flex items-center"
                          title={t('periods.viewDetails')}
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          {t('periods.details')}
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>

      {/* 對話框 */}
      <FiscalYearDialog
        isOpen={isFiscalYearDialogOpen}
        onClose={handleCloseFiscalYearDialog}
        fiscalYear={selectedFiscalYear}
        onFiscalYearCreated={handleFiscalYearCreated}
      />

      <PeriodClosingDialog
        isOpen={isPeriodClosingDialogOpen}
        onClose={() => setIsPeriodClosingDialogOpen(false)}
        availablePeriods={getAllAvailablePeriods()}
        onClosingStarted={handleClosingStarted}
      />

      <PeriodClosingDetailDialog
        isOpen={isPeriodClosingDetailDialogOpen}
        onClose={handleClosePeriodClosingDetailDialog}
        periodClosing={selectedPeriodClosing}
        onNotesUpdated={handleNotesUpdated}
      />

      {/* 重新開啟期間確認對話框 */}
      <ConfirmModal
        isOpen={showReopenModal}
        onClose={() => setShowReopenModal(false)}
        onConfirm={confirmReopenPeriod}
        title={t('periods.confirmModal.reopenTitle')}
        message={(() => {
          // 檢查是否為年結期間
          const isYearEndPeriod = periodClosings?.some(closing =>
            closing.periodId === periodToReopen?.id &&
            closing.type === 'YEARLY' &&
            closing.status === 'COMPLETED'
          );

          // 檢查是否因年結而關閉（該會計年度有年結記錄，但此期間本身沒有結算記錄）
          const hasYearEndInFiscalYear = periodClosings?.some(closing =>
            closing.period?.fiscalYear?.id === periodToReopen?.fiscalYear?.id &&
            closing.type === 'YEARLY' &&
            closing.status === 'COMPLETED'
          );

          const hasPeriodClosing = periodClosings?.some(closing =>
            closing.periodId === periodToReopen?.id &&
            closing.status === 'COMPLETED'
          );

          const isClosedByYearEnd = hasYearEndInFiscalYear && !hasPeriodClosing;

          if (isYearEndPeriod || isClosedByYearEnd) {
            const reason = isYearEndPeriod ? t('periods.confirmModal.reopenReasonYearEnd') : t('periods.confirmModal.reopenReasonClosedByYearEnd');
            return t('periods.confirmModal.reopenMessageYearEnd', {
              name: periodToReopen?.name,
              reason: reason,
              year: periodToReopen?.fiscalYear?.name || '-'
            });
          } else {
            return t('periods.confirmModal.reopenMessageSimple', { name: periodToReopen?.name });
          }
        })()}
        confirmText={t('periods.confirmModal.reopenConfirm')}
        cancelText={t('periods.confirmModal.cancel')}
        isLoading={reopening}
        loadingText={t('periods.confirmModal.reopenLoading')}
      />

      {/* 刪除期間確認對話框 */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeletePeriod}
        title={t('periods.confirmModal.deleteTitle')}
        message={t('periods.confirmModal.deleteMessage', { name: periodToDelete?.name })}
        confirmText={t('periods.confirmModal.deleteConfirm')}
        cancelText={t('periods.confirmModal.cancel')}
        isLoading={deleting}
        loadingText={t('periods.confirmModal.deleteLoading')}
        type="danger"
      />

      {/* 刪除會計年度確認對話框 */}
      <ConfirmModal
        isOpen={showDeleteFiscalYearModal}
        onClose={() => setShowDeleteFiscalYearModal(false)}
        onConfirm={confirmDeleteFiscalYear}
        title={t('periods.confirmModal.deleteFiscalYearTitle')}
        message={t('periods.confirmModal.deleteFiscalYearMessage', { name: fiscalYearToDelete?.name })}
        confirmText={t('periods.confirmModal.deleteFiscalYearConfirm')}
        cancelText={t('periods.confirmModal.cancel')}
        isLoading={deletingFiscalYear}
        loadingText={t('periods.confirmModal.deleteFiscalYearLoading')}
        type="danger"
      />
      
      {/* 開發工具：清理期間結算記錄確認對話框 */}
      {isDevEnv && (
        <ConfirmModal
          isOpen={showDevClearModal}
          onClose={() => setShowDevClearModal(false)}
          onConfirm={confirmClearPeriodClosings}
          title={t('periods.confirmModal.devClearTitle')}
          message={t('periods.confirmModal.devClearMessage')}
          confirmText={t('periods.confirmModal.devClearConfirm')}
          cancelText={t('periods.confirmModal.cancel')}
          isLoading={clearingData}
          loadingText={t('periods.confirmModal.devClearLoading')}
          type="danger"
        />
      )}
    </div>
  );
};

export default PeriodManagementPage;